import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Typography from "../common-components/Typography";
import content from "../constants/content";
import vmTheme from "../constants/theme";
import { font } from "../constants/font";
import { STATIC_PATH } from "../constants/config";
import Action from "../common-components/Action";
import { Background, Celebration, ExportSidebar, PublishFlex } from "./publish-component";
import ExportDetails from "./export-details";
import Youtube from "./youtube";
import Tiktok from "./tiktok";
import Progress from "./progress";
import { setStageClarity } from "../helper/trackClarity";
import TRACKERS from "../constants/trackers";
import useExport from "../helper/hooks/useExport";
import useWatermark from "../helper/hooks/useWatermark";
import useUpgrade from "../helper/hooks/useUpgrade";
import { PLAN, isShortsFlowSourceType } from "../constants";
import Exceed from "./Exceed";
import Instagram from "./instagram";
import trackerEvents from "../helper/trackerEvents";
import { isPaidUser } from "../helper/checkUser";

const Publish = ({ isShortsPlayer, shortId, isPublish }) => {
  const history = useHistory();
  const location = useLocation();
  const theme = useSelector((state) => state.app.get("theme"));
  const projectDetails = useSelector((store) => store.projectDetails);
  const { credits, plan, sourceType, projectId } = useSelector((state) => state.userDetails);
  const { totalCredits, usedCredits } = credits.socialpublishcount || {};
  const { totalCredits: exportTotalCredits, usedCredits: exportUsedCredits } = credits.exportcount || {};
  const [remainingCredits, setRemainingCredits] = useState({
    social: 0,
    export: 0
  });
  const [vcTriggered, setVcTriggered] = useState(false);
  const { triggerVC } = useExport();
  const { showPricingModal } = useUpgrade();
  const workspaceItems = projectDetails.get("workspaceItems");
  const isReframe = React.useMemo(() => {
    if (!workspaceItems) return false;

    return workspaceItems.valueSeq
      ? workspaceItems.valueSeq().some((item) => item.get("isReframe") === true)
      : Object.values(workspaceItems).some((item) => item.isReframe === true);
  }, [workspaceItems]);

  const shouldShowReframeUpgrade = isShortsFlowSourceType(sourceType)
    && plan === PLAN.LITE
    && isReframe;


  const showReframeUpgrade = (downloadPlatform) => {
    if (shouldShowReframeUpgrade) {
      showPricingModal({ type: "reframe", source: "free-upgrade-shorts", shortId });
      trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE, {
        projectId,
        shortId,
        downloadPlatform,
        isBoth: 1,
      });
      return true;
    }
    return false;
  };

  const upgradeBadge = (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
      <span>Upgrade</span>
      <img src={`${STATIC_PATH}yellow-star.svg`} alt="Star Icon" />
    </div>
  );

  const showWatermMark = useWatermark();

  const [isYoutube, setIsYoutube] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [isTikTok, setIsTikTok] = useState(false);
  const [isInstagram, setIsInstagram] = useState(false);

  useEffect(() => {
    setRemainingCredits({
      social: totalCredits - usedCredits,
      export: exportTotalCredits - exportUsedCredits
    })
  }, [totalCredits, usedCredits, exportTotalCredits, exportUsedCredits])

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const exportType = params.get("export_type");
    // To maintain the state on page refresh.
    if (exportType) {
      setIsDownload(exportType === "download");
      setIsYoutube(exportType === "youtube");
      setIsTikTok(exportType === "tiktok");
      setIsInstagram(exportType === "instagram");
    }
  }, [location.search]);

  useEffect(() => {
    if (!vcTriggered && projectId) {
      if (isYoutube || isTikTok) {
        triggerVC(true);
        setVcTriggered(true);
      } else if (isDownload) {
        triggerVC(false);
        setVcTriggered(true);
      }
    }
  }, [isYoutube, isTikTok, isDownload, isInstagram, triggerVC, vcTriggered, projectId]);

  /**
   * To Update the Query params
   * @param {string} type 
   */
  const updateExportType = (type) => {
    setVcTriggered(false); // Reset flag when manually switching platforms
    const params = new URLSearchParams(location.search);
    params.set("export_type", type);
    history.push({
      pathname: location.pathname,
      search: params.toString(),
      state: location.state, // Preserve existing state
    });
  };

  /**
   * To remove the query params
   */
  const removeExportType = () => {
    const params = new URLSearchParams(location.search);
    params.delete("export_type");
    history.push({
      pathname: location.pathname,
      search: params.toString(),
      state: location.state, // Preserve existing state
    });
  };

  const handleUpgradeClick = (type) => {
    showPricingModal({ type, source: "Direct-download" });
  };

  const onDownloadClick = () => {
    if (showReframeUpgrade("download")) return;
    trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE_PUBLISH_DOWNLOAD, {
      projectId,
      shortId,
      downloadPlatform: "download",
      isBoth: 1,
    });
    if (!isPaidUser(plan) || remainingCredits.export <= 0) {
      if (!isPaidUser(plan)) {
        trackerEvents(TRACKERS.EXPORT_LITE_PLAN_UPGRADE_PUBLISH, {
          projectId,
          shortId,
          downloadPlatform: "download",
          isBoth: 1,
        });
      }
      handleUpgradeClick("download");
    } else {
      updateExportType("download");
    }
    setStageClarity(TRACKERS.EXPORT_BTN_DOWNLOAD_CLICKED);
  };

  const onYouTubeClick = () => {
    if (showReframeUpgrade("youtube")) return;
    trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE_PUBLISH_YOUTUBE, {
      projectId,
      shortId,
      downloadPlatform: "youtube",
      isBoth: 1,
    });
    if (!isPaidUser(plan) || !remainingCredits.social) {
      if (!isPaidUser(plan)) {
        trackerEvents(TRACKERS.EXPORT_LITE_PLAN_UPGRADE_PUBLISH, {
          projectId,
          shortId,
          downloadPlatform: "youtube",
          isBoth: 1,
        });
      }
      handleUpgradeClick("socialPublish");
    } else {
      setStageClarity(TRACKERS.EXPORT_BTN_YOUTUBE_CLICKED);
      updateExportType("youtube");
    }
  };

  const onTikTokClick = () => {
    if (showReframeUpgrade("tiktok")) return;
    trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE_PUBLISH_TIKTOK, {
      projectId,
      shortId,
      downloadPlatform: "tiktok",
      isBoth: 1,
    });
    if (!isPaidUser(plan) || !remainingCredits.social) {
      if (!isPaidUser(plan)) {
        trackerEvents(TRACKERS.EXPORT_LITE_PLAN_UPGRADE_PUBLISH, {
          projectId,
          shortId,
          downloadPlatform: "tiktok",
          isBoth: 1,
        });
      }
      handleUpgradeClick("socialPublish");
    } else {
      setStageClarity(TRACKERS.EXPORT_BTN_TIKTOK_CLICKED);
      updateExportType("tiktok");
    }
  };

  const onInstagramClick = () => {
    if (showReframeUpgrade("instagram")) return;
    trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE_PUBLISH_INSTAGRAM, {
      projectId,
      shortId,
      platform: "publish",
      downloadPlatform: "instagram",
      isBoth: 1,
    });
    if (!isPaidUser(plan) || !remainingCredits.social) {
      if (!isPaidUser(plan)) {
        trackerEvents(TRACKERS.EXPORT_LITE_PLAN_UPGRADE_PUBLISH, {
          projectId,
          shortId,
          platform: "publish",
          downloadPlatform: "instagram",
          isBoth: 1,
        });
      }
      handleUpgradeClick("socialPublish");
    } else {
      setStageClarity(TRACKERS.EXPORT_BTN_INSTAGRAM_CLICKED);
      updateExportType("instagram");
      triggerVC(true);
    }
  };

  const onGoBack = () => {
    if (isYoutube) {
      setIsYoutube(false);
    } else if (isTikTok) {
      setIsTikTok(false);
    } else if (isInstagram) {
      setIsInstagram(false);
    } else {
      setIsDownload(false);
    }
    removeExportType();
  }

  if (isPublish) {
    return <Progress isTikTok={isTikTok} isInstagram={isInstagram} />;
  }

  if (isYoutube) {
    return <Youtube onBackAction={onGoBack} isShortsPlayer={isShortsPlayer} shortId={shortId} showWatermMark={showWatermMark} />
  }

  if (isTikTok) {
    return <Tiktok onBackAction={onGoBack} isShortsPlayer={isShortsPlayer} shortId={shortId} showWatermMark={showWatermMark} />
  }

  if (isInstagram) {
    return <Instagram onBackAction={onGoBack} isShortsPlayer={isShortsPlayer} shortId={shortId} showWatermMark={showWatermMark} />
  }

  if (isDownload) {
    return <ExportDetails
      goBack={onGoBack}
      showWatermMark={showWatermMark}
      isShortsPlayer={isShortsPlayer} shortId={shortId}
    />
  }

  return (
    <ExportSidebar
      remainingCredits={isPaidUser(plan) && remainingCredits.export !== 0 && remainingCredits.social !== 0 && totalCredits !== undefined}
      shouldShowReframeUpgrade={shouldShowReframeUpgrade || !isPaidUser(plan)}
    >
      <Typography
        content={content.EXPORT_VIDEO}
        color={vmTheme[theme].animoHeaderText}
        enableTrim={false}
        font={font.semiBold_45}
        display={"block"}
        align={"center"}
      />
      <Typography
        content={content.PUBLISH_TO_YOUTUBE}
        color={vmTheme[theme].exportLabelColor}
        enableTrim={false}
        font={font.normal14_400}
        display={"block"}
        align={"center"}
      />
      {!isPaidUser(plan) ?
        <Celebration margin="8px 0px 8px">
          <Typography
            content={content.DONT_LET_YOUR_FINISHED}
            color={vmTheme[theme].celebrationViolet}
            enableTrim={false}
            font={font.boldMini}
            display={"block"}
            width={"auto"}
            padding={"2px 40px 8px 0px"}
          />
        </Celebration> : null}
      <Background shouldShowReframeUpgrade={shouldShowReframeUpgrade || !isPaidUser(plan)}>
        <PublishFlex shouldShowReframeUpgrade={shouldShowReframeUpgrade || !isPaidUser(plan)}>
          <Typography
            innerContent={totalCredits - usedCredits === 1 ? "Publish to (1 publish left)" : "Publish to "}
            color={vmTheme[theme].sbSecondaryColor}
            enableTrim={false}
            font={font.mediumBase_21}
            padding={"0px 0px 8px"}
          />
          {(shouldShowReframeUpgrade || !isPaidUser(plan)) ?
            <Action
              text={content.UPGRADE}
              fontColor={vmTheme[theme].polarColor}
              background={vmTheme[theme].shortsPricingUpgradeColor}
              height={"auto"}
              width={"fit-content"}
              icon={`${STATIC_PATH}white-star.svg`}
              font={font.normal_600_11}
              hoverColor={vmTheme[theme].shortsPricingUpgradeColor}
              borderColor={vmTheme[theme].shortsPricingUpgradeColor}
              borderWidth={"1px"}
              borderRadius={"50px"}
              marginTop={"0px"}
              marginBottom={"8px"}
              top={"6px"}
              style={{ cursor: "pointer", padding: '0px 10px 8px 1px' }}
              onClick={onYouTubeClick}
            /> : null}
        </PublishFlex>
        <Action
          text={content.YOUTUBE}
          fontColor={vmTheme[theme].paymentCardTextColor}
          background={vmTheme[theme].polarColor}
          height={"auto"}
          width={"100%"}
          icon={`${STATIC_PATH}vm-youtube.svg`}
          font={font.mediumBase_21}
          hoverColor={vmTheme[theme].polarColor}
          borderColor={vmTheme[theme].socialBorderColor}
          borderWidth={"1px"}
          borderRadius={"12px"}
          marginTop={"0px"}
          marginBottom={"8px"}
          customClass={!isPaidUser(plan) || (remainingCredits.social === 0 && totalCredits !== undefined) ? "social-media upgrade" : "social-media"}
          top={"6px"}
          onClick={onYouTubeClick}
        >
          {isPaidUser(plan) && remainingCredits.social === 0 && totalCredits !== undefined
            ? upgradeBadge
            : null}
        </Action>
        <Action
          text={content.TIKTOK}
          fontColor={vmTheme[theme].paymentCardTextColor}
          background={vmTheme[theme].polarColor}
          height={"auto"}
          width={"100%"}
          icon={`${STATIC_PATH}tiktok.svg`}
          font={font.mediumBase_21}
          hoverColor={vmTheme[theme].polarColor}
          borderColor={vmTheme[theme].socialBorderColor}
          borderWidth={"1px"}
          borderRadius={"12px"}
          marginTop={"0px"}
          marginBottom={"8px"}
          customClass={!isPaidUser(plan) || (remainingCredits.social === 0 && totalCredits !== undefined) ? "social-media tiktok upgrade" : "social-media tiktok"}
          top={"6px"}
          onClick={onTikTokClick}
        >
          {isPaidUser(plan) && remainingCredits.social === 0 && totalCredits !== undefined
            ? upgradeBadge
            : null}
        </Action>
        <Action
          text={content.INSTAGRAM}
          fontColor={vmTheme[theme].paymentCardTextColor}
          background={vmTheme[theme].polarColor}
          height={"auto"}
          width={"100%"}
          icon={`${STATIC_PATH}instagram.svg`}
          font={font.mediumBase_21}
          hoverColor={vmTheme[theme].polarColor}
          borderColor={vmTheme[theme].socialBorderColor}
          borderWidth={"1px"}
          borderRadius={"12px"}
          marginTop={"0px"}
          marginBottom={"8px"}
          customClass={!isPaidUser(plan) || (remainingCredits.social === 0 && totalCredits !== undefined) ? "social-media instagram upgrade" : "social-media instagram"}
          top={"6px"}
          onClick={onInstagramClick}
        >
          {isPaidUser(plan) && remainingCredits.social === 0 && totalCredits !== undefined
            ? upgradeBadge
            : null}
        </Action>
        <Exceed
          showExceed={remainingCredits.social === 0 && totalCredits}
          content={`${totalCredits}/${usedCredits} publish Limit Exceeded`}
        />

        <Typography
          content={content.DIRECT_DOWNLOAD}
          color={vmTheme[theme].sbSecondaryColor}
          enableTrim={false}
          font={font.mediumBase_21}
          padding={"8px 0px"}
        />

        <Action
          text={content.DOWNLOAD}
          fontColor={vmTheme[theme].paymentCardTextColor}
          background={vmTheme[theme].polarColor}
          height={"auto"}
          width={"100%"}
          icon={`${STATIC_PATH}download.svg`}
          font={font.mediumBase_21}
          hoverColor={vmTheme[theme].polarColor}
          borderColor={vmTheme[theme].socialBorderColor}
          borderWidth={"1px"}
          borderRadius={"12px"}
          marginTop={"0px"}
          customClass={!isPaidUser(plan) || (remainingCredits.export === 0 && exportTotalCredits !== undefined) ? "social-media upgrade" : "social-media"}
          top={"6px"}
          onClick={onDownloadClick}
        />
        <Exceed
          showExceed={remainingCredits.export === 0 && exportTotalCredits}
          content={`${exportTotalCredits}/${exportUsedCredits} download Limit Exceeded`}
        />
      </Background>
    </ExportSidebar>
  );
};

Publish.propTypes = {
  isShortsPlayer: PropTypes.bool,
  shortId: PropTypes.string,
  isPublish: PropTypes.bool
}

export default Publish;
